import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';


// 3rd party components
import { AutoCompleteModule } from 'primeng/autocomplete';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ButtonsModule } from 'ngx-bootstrap/buttons';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { CalendarModule } from 'primeng/calendar';
import { NgChartsModule } from 'ng2-charts';
import { BsModalRef, ModalModule } from 'ngx-bootstrap/modal';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { CurrencyMaskConfig, CurrencyMaskModule, CURRENCY_MASK_CONFIG } from 'ng2-currency-mask';
import { NgxMaskModule, IConfig } from 'ngx-mask';
import { AngularEditorModule } from '@kolkov/angular-editor';
import { QrCodeModule } from 'ng-qrcode';
import { NgSelectConfig, NgSelectModule } from '@ng-select/ng-select';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';

import { FieldInputSelectModule } from 'src/app/shared/components/field-input-select/field-input-select.module';

// PrimeNG
import { InputSwitchModule } from 'primeng/inputswitch';

// Maps
import { GoogleMapsModule } from '@angular/google-maps';

import {
  CanDeactivateGuard
} from 'src/app/shared/guards';

import {
  MomentPipe, MoneyPipe, PermissionPipe, RenameCaptionPipe, SuperUserPipe
} from 'src/app/shared/pipes';

import { MASK_PROVIDE } from 'src/app/shared/utils';
import {
  NgVarDirective, FocusDirective, ImageCacheDirective, FileDropDirective,
  ControlValueAccessorDirective
} from 'src/app/shared/directive';
import { BRStatesProvider } from 'src/app/shared/provider';

// Components
import {
  AutoCompleteComponent, BottomFormNavigationComponent, DataTableComponent, DatepickerComponent,
  EmailComponent, FixedScrollComponent, FilterModalComponent, PaginationComponent,
  RegisterHeaderComponent, SearchHeaderComponent,  StatusComponent, SwitchComponent,
  MonthpickerComponent,
  NotFoundComponent, PhoneComponent, RequestPermissionComponent,
  // Modal
  ModalQuestionComponent, ModalSelectUserComponent, ModalInputComponent,
  ModalNameComponent, ModalQRCodeComponent,
  // Fields
  FieldErrorDisplayComponent,
  FieldInputTextComponent,
  FieldInputDateComponent,
  FieldInputSwitchComponent,
  FieldInputNgSelectComponent,
  FieldInputCurrencyComponent,
  FieldInputTextMaskComponent,
  FieldInputTextAreaComponent,
  // Maps
  MapsPolygonComponent,
  MapsMarkComponent,
} from 'src/app/shared/components';

import { PdfViewerComponent } from 'src/app/shared/components/pdf-viewer/pdf-viewer.component'
import { SendToWaComponent } from 'src/app/shared/components/send-to-wa/send-to-wa.component';

// Services
import {
  PermissionService, RouterService, ToastrMsgService, ResourceIdListService, RenameCaptionService
} from 'src/app/shared/services';
import { FilterModalService } from './components/search-header/filter-modal';
import { ModalInputService } from 'src/app/shared/components/modal-input/shared/service';
import { ModalQuestionService } from 'src/app/shared/components/modal-question/shared/service';
import { ModalSelectUserService } from 'src/app/shared/components/modal-select-user/shared';
import { RequestPermissionService } from 'src/app/shared/components/request-permission/shared/service';
import { ModalNameService } from 'src/app/shared/components/modal-name';
import { ModalQRCodeService } from 'src/app/shared/components/modal-qrcode/shared/service';
import { MapsPolygonService, MapsMarkService, GoogleMapsLoaderService } from 'src/app/shared/components';
import { PDFService } from 'src/app/shared/services/pdf.service';
import { ModalPDFViewerService } from 'src/app/shared/components/pdf-viewer/pdf-viewer.service'
import { SendToWAModalService } from 'src/app/shared/components/send-to-wa/send-to-wa.modal.service';
import { WAService } from 'src/app/shared/components/send-to-wa/send-to-wa.service';

// Locales
import { defineLocale } from 'ngx-bootstrap/chronos';
import { ptBrLocale } from 'ngx-bootstrap/locale';

defineLocale(ptBrLocale.abbr, ptBrLocale);

export const CustomCurrencyMaskConfig: CurrencyMaskConfig = {
  align: 'left',
  allowNegative: true,
  decimal: ',',
  precision: 2,
  prefix: 'R$ ',
  suffix: '',
  thousands: '.'
};

const maskConfig: Partial<IConfig> = {
  validation: false,
};

const THREE_PARTY_COMP = [
  AutoCompleteModule,
  ButtonsModule,
  BsDropdownModule,
  CalendarModule,
  NgChartsModule,
  ModalModule,
  TabsModule,
  NgxMaskModule,
  CurrencyMaskModule,
  AngularEditorModule,
  QrCodeModule,
  NgSelectModule,
  NgxExtendedPdfViewerModule,
  // Prime NG
  InputSwitchModule
];

const SERVICES = [
  FilterModalService,
  ModalInputService,
  ModalQuestionService,
  ModalSelectUserService,
  PDFService,
  PermissionService,
  RenameCaptionService,
  ResourceIdListService,
  RequestPermissionService,
  RouterService,
  ToastrMsgService,
  ModalNameService,
  ModalQRCodeService,
  MapsPolygonService,
  MapsMarkService,
  GoogleMapsLoaderService,
  ModalPDFViewerService,
  SendToWAModalService,
  WAService
];

const PROVIDERS = [
  BsModalRef,
  BRStatesProvider
];

const MODAL_COMPONENTS = [
  FilterModalComponent,
  ModalQuestionComponent,
  ModalSelectUserComponent,
  ModalInputComponent,
  ModalNameComponent,
  ModalQRCodeComponent,
  MapsPolygonComponent,
  MapsMarkComponent,
  RequestPermissionComponent,
  PdfViewerComponent,
  SendToWaComponent
];

const COMPONENTS = [
  ...MODAL_COMPONENTS,
  AutoCompleteComponent,
  BottomFormNavigationComponent,
  DataTableComponent,
  DatepickerComponent,
  EmailComponent,
  FixedScrollComponent,
  MonthpickerComponent,
  NotFoundComponent,
  PaginationComponent,
  PhoneComponent,
  RegisterHeaderComponent,
  // RequestPermissionComponent,
  SearchHeaderComponent,
  StatusComponent,
  SwitchComponent,
  PdfViewerComponent,
  SendToWaComponent,

  // Fields
  FieldErrorDisplayComponent,
  FieldInputTextComponent,
  FieldInputDateComponent,
  FieldInputSwitchComponent,
  FieldInputNgSelectComponent,
  FieldInputCurrencyComponent,
  FieldInputTextMaskComponent,
  FieldInputTextAreaComponent,

  // Maps
  MapsPolygonComponent,
  MapsMarkComponent
];

const DIRECTIVE = [
  FocusDirective,
  ImageCacheDirective,
  NgVarDirective,
  FileDropDirective,
  ControlValueAccessorDirective
];

const PIPES = [
  MomentPipe,
  MoneyPipe,
  PermissionPipe,
  RenameCaptionPipe,
  SuperUserPipe
];

@NgModule({
    imports: [
        AutoCompleteModule,
        CalendarModule,
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        ButtonsModule.forRoot(),
        BsDropdownModule.forRoot(),
        ModalModule.forRoot(),
        PaginationModule.forRoot(),
        PopoverModule.forRoot(),
        BsDatepickerModule.forRoot(),
        TabsModule.forRoot(),
        CurrencyMaskModule,
        NgxMaskModule.forRoot(maskConfig),
        GoogleMapsModule,
        QrCodeModule,
        NgSelectModule,
        NgxExtendedPdfViewerModule,
        InputSwitchModule,
        FieldInputSelectModule,
    ],
    declarations: [
        ...COMPONENTS,
        ...DIRECTIVE,
        ...PIPES,
    ],
    providers: [
        CanDeactivateGuard,
        MASK_PROVIDE,
        ...SERVICES,
        ...PROVIDERS,
        { provide: CURRENCY_MASK_CONFIG, useValue: CustomCurrencyMaskConfig },
    ],
    exports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        FieldInputSelectModule,
        ...COMPONENTS,
        ...DIRECTIVE,
        ...PIPES,
        ...THREE_PARTY_COMP,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SharedModule {
  constructor(private config: NgSelectConfig) {
    this.config.notFoundText = 'Não encontrado';
    this.config.appendTo = 'body';
    // set the bindValue to global config when you use the same
    // bindValue in most of the place.
    // You can also override bindValue for the specified template
    // by defining `bindValue` as property
    // Eg : <ng-select bindValue="some-new-value"></ng-select>
    this.config.bindValue = 'value';
    this.config.loadingText = 'Carregando...';
}
}
